import type { MusicNotes, MusicPart, MusicTrack } from '../types';
import { hitsAt } from './track-kit';

const HUNGARIAN_MINOR = [0, 2, 3, 6, 7, 8, 11];

const AILES_OSTINATO: MusicNotes = [
  [0, 4, 1],
  [1, 4, 1],
  [2, 0, 1],
  [3, 4, 1],
  [4, 4, 1],
  [5, 0, 1],
  [6, 4, 1],
  [7, 0, 1],
  [8, 4, 1],
  [9, 4, 1],
  [10, 0, 1],
  [11, 4, 1],
  [12, 0, 1],
  [13, 4, 1],
  [14, 0, 1],
  [15, 0, 1],
  [16, 4, 1],
  [17, 4, 1],
  [18, 0, 1],
  [19, 4, 1],
  [20, 4, 1],
  [21, 0, 1],
  [22, 4, 1],
  [23, 0, 1],
  [24, 4, 1],
  [25, 2, 1],
  [26, 0, 1],
  [27, 3, 1],
  [28, 0, 1],
  [29, 4, 1],
  [30, -1, 1],
  [31, 0, 1],
  [32, 4, 1],
  [33, 4, 1],
  [34, 0, 1],
  [35, 4, 1],
  [36, 4, 1],
  [37, 0, 1],
  [38, 4, 1],
  [39, 0, 1],
  [40, 4, 1],
  [41, 4, 1],
  [42, 0, 1],
  [43, 4, 1],
  [44, 0, 1],
  [45, 4, 1],
  [46, 0, 1],
  [47, 0, 1],
  [48, 5, 2],
  [50, 0, 1],
  [51, 5, 2],
  [53, 0, 1],
  [54, 5, 2],
  [56, 4, 2],
  [58, 0, 1],
  [59, 3, 2],
  [61, 2, 1],
  [62, 3, 1],
  [63, 0, 1],
];

const AILES_CHANT: MusicNotes = [
  [0, 0, 8],
  [8, -1, 2],
  [10, 0, 14],
  [32, 5, 4],
  [36, 4, 2],
  [38, 5, 2],
  [40, 6, 4],
  [44, 7, 16],
];

const AILES_PAD: MusicPart[] = [0, 2, 4].map((degree) => ({
  voice: 'saw-pad',
  layer: 'texture',
  loopSteps: 32,
  octave: 1,
  followsChord: true,
  notes: [[0, degree, 32]],
  in: 'light',
  gain: 0.75,
}));

const LES_AILES_DE_BOIS: MusicTrack = {
  id: 'les-ailes-de-bois',
  name: 'Les ailes de bois',
  rootMidi: 38,
  scale: HUNGARIAN_MINOR,
  chords: [0, 0, 0, 0, 0, 0, -3, -3],
  kick: { fromHz: 180, release: 0.3 },
  echo: { feedback: 0.34, ret: 0.24, tone: 3600, wide: true },
  padOpens: 60,
  build: 'soft',
  drums: [
    { voice: 'tuned-kick', layer: 'kick', loopSteps: 16, hits: hitsAt([0, 4, 8, 12]), hz: 55 },
    {
      voice: 'bright-hat-open',
      layer: 'kick',
      loopSteps: 16,
      hits: hitsAt([2, 6, 10, 14], 0.9),
      pan: 0.35,
    },
    {
      voice: 'bright-hat',
      layer: 'kick',
      loopSteps: 16,
      hits: [
        [1, 0.45],
        [3, 0.35],
        [5, 0.45],
        [7, 0.35],
        [9, 0.45],
        [11, 0.35],
        [13, 0.45],
        [15, 0.4],
      ],
      pan: -0.45,
    },
    { voice: 'big-clap', layer: 'kick', loopSteps: 16, hits: hitsAt([4, 12], 0.8) },
    {
      voice: 'sand-shaker',
      layer: 'texture',
      loopSteps: 4,
      hits: [
        [0, 0.4],
        [1, 0.7],
        [2, 0.5],
        [3, 0.9],
      ],
      in: 'full',
      pan: 0.55,
    },
    {
      voice: 'wood-rim',
      layer: 'arp',
      loopSteps: 16,
      hits: [
        [3, 0.4],
        [10, 0.45],
        [14, 0.3],
      ],
      pan: 0.4,
    },
    { voice: 'ride', layer: 'clap', loopSteps: 16, hits: hitsAt([2, 6, 10, 14], 0.7), pan: -0.55 },
  ],
  parts: [
    {
      voice: 'gallop-bass',
      layer: 'bass',
      loopSteps: 4,
      octave: 0,
      followsChord: true,
      notes: [
        [1, 0, 1],
        [2, 0, 1],
        [3, 0, 1],
      ],
    },
    {
      voice: 'pulse',
      layer: 'texture',
      loopSteps: 4,
      octave: 1,
      followsChord: false,
      notes: [
        [0, 0, 1],
        [1, 0, 1],
        [2, 0, 1],
        [3, 0, 1],
      ],
      in: 'full',
      gain: 1.1,
    },
    {
      voice: 'sweep-pluck',
      layer: 'lead',
      loopSteps: 64,
      octave: 1,
      followsChord: false,
      notes: AILES_OSTINATO,
      gain: 0.8,
    },
    {
      voice: 'sweep-pluck',
      layer: 'theme',
      loopSteps: 64,
      octave: 1,
      followsChord: false,
      notes: AILES_OSTINATO,
      accents: [0, 16, 32, 48],
      gain: 1.5,
    },
    ...AILES_PAD,
    {
      voice: 'chant',
      layer: 'texture',
      loopSteps: 64,
      octave: 1,
      followsChord: false,
      notes: AILES_CHANT,
      slides: [8, 10, 36, 38, 40, 44],
      in: 'light',
      gain: 0.8,
    },
  ],
};

export const PUMPUI_TRACKS: readonly MusicTrack[] = [LES_AILES_DE_BOIS];
